// Microsoft Graph PKCE OAuth flow
// The app-level auth (Microsoft identity) is handled by Cloudflare Access.
// This is a *separate* OAuth consent specifically for OneDrive file access.

const TENANT = 'consumers'  // personal Microsoft accounts
const SCOPES = ['Files.ReadWrite', 'offline_access', 'User.Read'].join(' ')
const GRAPH = 'https://graph.microsoft.com/v1.0'

function getClientId(): string {
  return import.meta.env.VITE_ONEDRIVE_CLIENT_ID ?? ''
}

function getRedirectUri(): string {
  return import.meta.env.VITE_ONEDRIVE_REDIRECT_URI
    ?? `${window.location.origin}/onedrive/callback`
}

// PKCE helpers
async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

export async function startOneDriveAuth(): Promise<void> {
  const verifier = await generateCodeVerifier()
  const challenge = await generateCodeChallenge(verifier)
  sessionStorage.setItem('od_verifier', verifier)

  const params = new URLSearchParams({
    client_id: getClientId(),
    response_type: 'code',
    redirect_uri: getRedirectUri(),
    scope: SCOPES,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    response_mode: 'query',
  })

  window.location.href = `https://login.microsoftonline.com/${TENANT}/oauth2/v2.0/authorize?${params}`
}

export async function handleOneDriveCallback(code: string): Promise<void> {
  const verifier = sessionStorage.getItem('od_verifier')
  if (!verifier) throw new Error('Missing PKCE verifier')
  sessionStorage.removeItem('od_verifier')

  // Exchange code via our Worker (keeps the token server-side)
  const res = await fetch('/api/onedrive/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, verifier, redirect_uri: getRedirectUri() }),
  })
  if (!res.ok) throw new Error(await res.text())
}

// Direct Graph API call (Worker proxies with stored token)
export async function graphRequest<T>(path: string): Promise<T> {
  const res = await fetch(`/api/onedrive/graph?path=${encodeURIComponent(path)}`)
  if (!res.ok) throw new Error(await res.text())
  return res.json()
}

export { GRAPH }
